import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import useWorkspaceCacheUpdate from "common/datahooks/useWorkspaceCacheUpdate";
import { del, post } from "common/helpers/HTTP";
import { getOutreachLicenseCount } from "common/helpers/workspaceLicenseCounts";
import type {
  DetailedWorkspace,
  RentedSubscription,
  Subscription,
  SubscriptionType,
  WorkspaceAccount,
} from "common/types";

type CreateSubscriptionResponse = {
  workspace?: DetailedWorkspace;
  subscription?: Subscription | RentedSubscription;
};

async function createSubscriptionRequest(
  workspaceId: string,
  planId: string,
  count: number,
  type: SubscriptionType,
  paymentSourceId: string,
  couponId?: string,
): Promise<CreateSubscriptionResponse> {
  const response = await post(`workspaces/${workspaceId}/subscriptions`, {
    item_price_id: planId,
    item_count: count,
    type,
    payment_source_id: paymentSourceId,
    coupon: couponId,
  });

  return {
    workspace: response.workspace,
    subscription: response.subscription,
  };
}

async function removeChangeRequest(
  workspaceId: string,
  subscriptionId: string,
): Promise<{ workspace: DetailedWorkspace; accounts: WorkspaceAccount[] }> {
  const {
    workspace: { accounts, ...detailedWorkspace },
  } = await del(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/changes`,
  );

  return { workspace: detailedWorkspace, accounts };
}

type CancelSubscriptionResponse = {
  status: string;
  subscription: Subscription | RentedSubscription;
};

function cancelSubscriptionRequest(
  workspaceId: string,
  subscriptionId: string,
  reason: string,
): Promise<CancelSubscriptionResponse> {
  return post(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/cancel`,
    { reason },
  );
}

export default function useWorkspaceSubscriptionMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const {
    updateCachedWorkspaceDetails,
    updateCachedWorkspaceList,
    updateCachedWorkspaceAccounts,
  } = useWorkspaceCacheUpdate();

  const {
    mutateAsync: createWorkspaceSubscription,
    isPending: isCreatingWorkspaceSubscription,
    error: createWorkspaceSubscriptionError,
  } = useMutation({
    mutationFn: ({
      planId,
      count,
      type,
      paymentSourceId,
      couponId,
    }: {
      planId: string;
      count: number;
      type: SubscriptionType;
      paymentSourceId: string;
      couponId?: string;
    }) =>
      createSubscriptionRequest(
        workspaceId,
        planId,
        count,
        type,
        paymentSourceId,
        couponId,
      ),
    onSuccess: ({ workspace, subscription }, { type, count }) => {
      if (workspace) {
        updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
          Object.assign(draftWorkspace, workspace);
          if (type === "rental") {
            draftWorkspace.has_active_rental_subscription = true;
          }
          if (type === "user") {
            draftWorkspace.outreach_license_count =
              getOutreachLicenseCount(workspace, count) ?? count;
          }
        });
      } else if (subscription) {
        updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
          if (subscription.type === "rental") {
            const rentedSubscription = subscription as RentedSubscription;
            const subscriptions = draftWorkspace.rental_subscriptions ?? [];
            const index = subscriptions.findIndex(
              ({ id }) => id === rentedSubscription.id,
            );

            if (index === -1) {
              draftWorkspace.rental_subscriptions = [
                ...subscriptions,
                rentedSubscription,
              ];
            } else {
              draftWorkspace.rental_subscriptions[index] = rentedSubscription;
            }

            draftWorkspace.total_rented_count =
              draftWorkspace.rental_subscriptions.reduce(
                (total, { rented_count }) => total + rented_count,
                0,
              );
            draftWorkspace.has_active_rental_subscription = true;
          } else {
            draftWorkspace.subscription = subscription as Subscription;
            if (type === "user") {
              draftWorkspace.outreach_license_count = count;
              draftWorkspace.subscription_status = subscription.status;
            }
          }
        });
      }

      updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
        if (type === "user") {
          draftWorkspace.subscription_status =
            workspace?.subscription?.status ?? subscription?.status ?? "active";
          draftWorkspace.outreach_license_count =
            getOutreachLicenseCount(workspace, count) ?? count;
        } else if (type === "rental") {
          draftWorkspace.has_active_rental_subscription = true;
          if (workspace?.rental_account_count !== undefined) {
            draftWorkspace.rental_account_count =
              workspace.rental_account_count;
          }
        }
      });
    },
  });

  const { mutateAsync: removeChange, isPending: isRemovingChange } =
    useMutation({
      mutationFn: ({
        subscription,
      }: {
        subscription: Subscription | RentedSubscription;
      }) => removeChangeRequest(workspaceId, subscription.id),
      onSuccess: ({ workspace, accounts }, { subscription }) => {
        updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
          const previousOutreach = draftWorkspace.outreach_license_count;
          Object.assign(draftWorkspace, workspace);
          if (subscription.type === "user") {
            const outreachCount = getOutreachLicenseCount(
              workspace,
              previousOutreach,
            );
            if (outreachCount != null) {
              draftWorkspace.outreach_license_count = outreachCount;
            }
          }
        });
        updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
          if (workspace.subscription) {
            draftWorkspace.subscription_status = workspace.subscription.status;
          }
          if (workspace.has_active_rental_subscription !== undefined) {
            draftWorkspace.has_active_rental_subscription =
              workspace.has_active_rental_subscription;
          }
          if (workspace.rental_account_count !== undefined) {
            draftWorkspace.rental_account_count =
              workspace.rental_account_count;
          }
        });
        updateCachedWorkspaceAccounts(workspaceId, () => {
          return accounts;
        });

        queryClient.invalidateQueries({
          queryKey: getQueryKeys(workspaceId).subscriptionKeys.renewalEstimate(
            subscription.id,
          ),
        });
      },
      onError: () => toast.error("Failed to remove scheduled change"),
    });

  const {
    mutateAsync: cancelSubscription,
    isPending: isCancelingSubscription,
  } = useMutation({
    mutationFn: ({
      subscriptionId,
      reason,
    }: {
      subscriptionId: string;
      reason: string;
    }) => cancelSubscriptionRequest(workspaceId, subscriptionId, reason),
    onSuccess: ({ subscription }) => {
      if (subscription.type === "rental") {
        updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
          const index = draftWorkspace.rental_subscriptions.findIndex(
            ({ id }) => id === subscription.id,
          );
          if (index !== -1) {
            draftWorkspace.rental_subscriptions[index] =
              subscription as RentedSubscription;
          }
        });
        queryClient.invalidateQueries({
          queryKey: CUSTOMER_QUERY_KEYS.workspaces.base,
        });
      } else {
        updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
          draftWorkspace.subscription = subscription as Subscription;
          draftWorkspace.subscription_status = subscription.status;
        });
        updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
          draftWorkspace.subscription_status = subscription.status;
        });
      }
    },
  });

  return {
    createWorkspaceSubscription,
    isCreatingWorkspaceSubscription,
    createWorkspaceSubscriptionError,

    removeChange,
    isRemovingChange,

    cancelSubscription,
    isCancelingSubscription,
  };
}
